/* Shared primitives. Logical properties only (NFR-UX-03). */

/* ── Buttons ─────────────────────────────────────────────── */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-block-size: var(--touch-target);
  padding-inline: var(--space-4);
  border-radius: var(--radius-full);
  border: 1px solid var(--border);
  background: var(--surface-0);
  color: var(--text-1);
  font-weight: 600;
  white-space: nowrap;
  transition:
    background var(--motion-fast),
    transform var(--motion-fast);
}
.button:hover:not(:disabled) {
  background: var(--surface-1);
}
.button:active:not(:disabled) {
  transform: scale(0.97);
}
.button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--accent-fg);
}
.primary:hover:not(:disabled) {
  background: var(--accent);
  filter: brightness(1.08);
}
.danger {
  background: var(--danger);
  border-color: var(--danger);
  color: var(--danger-fg);
}
.danger:hover:not(:disabled) {
  background: var(--danger);
  filter: brightness(1.08);
}
.ghost {
  background: transparent;
  border-color: transparent;
}
.ghost:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
.lg {
  min-block-size: 52px;
  font-size: var(--font-size-3);
  padding-inline: var(--space-5);
}
.iconOnly {
  padding-inline: 0;
  inline-size: var(--touch-target);
  flex: none;
}
.spinner,
.spinnerLarge {
  inline-size: 18px;
  block-size: 18px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-inline-end-color: transparent;
  animation: spin 700ms linear infinite;
}
.spinnerLarge {
  inline-size: 32px;
  block-size: 32px;
  border-width: 3px;
  color: var(--accent);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Fields ──────────────────────────────────────────────── */
.field {
  display: grid;
  gap: var(--space-1);
}
.fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-2);
}
.label {
  font-weight: 600;
  font-size: var(--font-size-2);
  padding: 0;
}
.input {
  font: inherit;
  font-size: max(16px, var(--font-size-2)); /* ≥16px avoids iOS zoom on focus */
  inline-size: 100%;
  min-block-size: var(--touch-target);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-2);
  background: var(--surface-0);
  color: var(--text-1);
}
textarea.input {
  resize: vertical;
  min-block-size: 72px;
}
.input[aria-invalid='true'] {
  border-color: var(--danger);
}
.hint {
  margin: 0;
  color: var(--text-2);
  font-size: var(--font-size-1);
}
.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--font-size-1);
  font-weight: 600;
}

/* Quantity stepper */
.stepper {
  display: grid;
  grid-template-columns: var(--touch-target) minmax(64px, 1fr) var(--touch-target);
  border: 1px solid var(--border);
  min-block-size: var(--touch-target);
  border-radius: var(--radius-2);
  overflow: hidden;
  inline-size: 100%;
  background: var(--surface-0);
}
.stepper button {
  border: 0;
  border-radius: 0;
  padding: 0;
  display: grid;
  place-items: center;
  background: var(--surface-1);
}
.stepper input {
  border: 0;
  text-align: center;
  font: inherit;
  font-size: max(16px, var(--font-size-3));
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: var(--surface-0);
  color: var(--text-1);
  min-inline-size: 0;
}
.stepper input:focus-visible {
  outline-offset: -3px;
}

/* Colour swatches */
.swatches {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--touch-target), 1fr));
  gap: var(--space-2);
}
.swatch {
  display: grid;
  place-items: center;
  min-block-size: var(--touch-target);
  cursor: pointer;
}
.swatchDot {
  inline-size: 40px;
  block-size: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--list-bg);
  color: var(--list-fg);
  border: 2px solid color-mix(in srgb, var(--list-fg) 35%, transparent);
}
.swatch:has(input:checked) .swatchDot {
  border-color: var(--list-fg);
  box-shadow:
    0 0 0 2px var(--surface-0),
    0 0 0 4px var(--list-fg);
}
.swatch:has(input:focus-visible) .swatchDot {
  outline: 3px solid var(--focus-ring);
  outline-offset: 3px;
}

/* ── Chips ───────────────────────────────────────────────── */
.chips {
  display: flex;
  gap: var(--space-2);
  overflow-x: auto;
  padding-block: var(--space-1);
  scrollbar-width: none;
  flex: none;
  min-block-size: 48px;
  align-items: center;
  margin-inline: calc(var(--space-4) * -1);
  padding-inline: var(--space-4);
}
.chips::-webkit-scrollbar {
  display: none;
}
.chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-block-size: 40px;
  padding-inline: var(--space-4);
  border-radius: var(--radius-full);
  border: 1px solid var(--border);
  background: var(--surface-0);
  color: var(--text-1);
  font-weight: 600;
  font-size: var(--font-size-2);
}
.chip[aria-checked='true'] {
  background: var(--text-1);
  border-color: var(--text-1);
  color: var(--surface-0);
}
.chipCount {
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}

/* ── Dialogs ─────────────────────────────────────────────── */
.dialog {
  border: 0;
  padding: 0;
  color: var(--text-1);
  background: var(--surface-0);
  max-inline-size: 100vw;
  max-block-size: 100dvh;
}
.dialog::backdrop {
  background: rgb(10 14 12 / 0.45);
}
.dialogBody {
  display: flex;
  flex-direction: column;
  max-block-size: inherit;
  block-size: 100%;
}
.dialogHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-3) var(--space-2) var(--space-4);
}
.dialogTitle {
  flex: 1;
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--font-size-4);
  line-height: 1.2;
}
.dialogContent {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-2) var(--space-4) var(--space-4);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-content: start;
  /* Rows keep their content height: without this, a scrolling child (e.g. the chip row) is
     squashed to nothing when the content is taller than the dialog. */
  grid-auto-rows: max-content;
}
.dialogFooter {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4) calc(var(--space-3) + env(safe-area-inset-bottom));
  border-block-start: 1px solid var(--border);
}
.dialogFooter > * {
  flex: 1;
}

/* Phones: sheets rise from the bottom; panels take the whole screen */
.dialog-sheet,
.dialog-alert {
  inline-size: 100%;
  margin: auto 0 0;
  border-start-start-radius: var(--radius-3);
  border-start-end-radius: var(--radius-3);
  max-block-size: 92dvh;
  animation: rise var(--motion-base) ease-out;
}
.dialog-panel {
  inline-size: 100%;
  block-size: 100dvh;
  margin: 0;
  animation: rise var(--motion-base) ease-out;
}
@keyframes rise {
  from {
    transform: translateY(24px);
    opacity: 0.4;
  }
}
.dialog-alert .dialogContent p {
  margin: 0;
}

@media (min-width: 600px) {
  .dialog-sheet,
  .dialog-alert {
    inline-size: min(520px, calc(100vw - var(--space-6)));
    margin: auto;
    border-radius: var(--radius-3);
    max-block-size: 85dvh;
  }
  .dialog-alert {
    inline-size: min(420px, calc(100vw - var(--space-6)));
  }
  .dialog-sheet.dialogWide {
    inline-size: min(720px, calc(100vw - var(--space-6)));
  }
  .dialogFooter > * {
    flex: none;
  }
  .dialog-panel {
    inline-size: min(480px, 100vw);
    margin-inline-start: auto;
    margin-inline-end: 0;
    block-size: 100dvh;
    max-block-size: 100dvh;
    border-start-start-radius: var(--radius-3);
    border-end-start-radius: var(--radius-3);
    animation: slide-in var(--motion-base) ease-out;
  }
}
@keyframes slide-in {
  from {
    translate: 32px 0;
    opacity: 0.4;
  }
}
:global([dir='rtl']) .dialog-panel {
  animation-name: slide-in-rtl;
}
@keyframes slide-in-rtl {
  from {
    translate: -32px 0;
    opacity: 0.4;
  }
}

/* ── Menu ───────────────────────────────────────────────── */
.menuRoot {
  position: relative;
  display: inline-flex;
}
.menu {
  position: absolute;
  inset-block-start: calc(100% + 4px);
  inset-inline-end: 0;
  z-index: 20;
  min-inline-size: 220px;
  margin: 0;
  padding: var(--space-1);
  list-style: none;
  background: var(--surface-0);
  border: 1px solid var(--border);
  border-radius: var(--radius-2);
  box-shadow: 0 12px 32px rgb(0 0 0 / 0.16);
}
.menuItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  inline-size: 100%;
  min-block-size: var(--touch-target);
  padding-inline: var(--space-3);
  border: 0;
  border-radius: var(--radius-1);
  background: transparent;
  color: var(--text-1);
  text-align: start;
  font-weight: 500;
}
.menuItem:hover,
.menuItem:focus-visible {
  background: var(--surface-2);
  outline-offset: -2px;
}
.menuDanger {
  color: var(--danger);
}

/* ── Toasts ─────────────────────────────────────────────── */
.toaster {
  position: fixed;
  inset-inline: var(--space-3);
  /* above the bottom navigation and the floating action button */
  inset-block-end: calc(58px + 56px + var(--space-6) + env(safe-area-inset-bottom));
  z-index: 50;
  display: grid;
  justify-items: center;
  pointer-events: none;
}
.toaster > div {
  display: grid;
  gap: var(--space-2);
  justify-items: center;
}
.toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  max-inline-size: min(480px, 100%);
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
  border-radius: var(--radius-2);
  background: var(--text-1);
  color: var(--surface-0);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.2);
  animation: rise var(--motion-base) ease-out;
}
.toast-error {
  background: var(--danger);
  color: var(--danger-fg);
}
.toastMessage {
  flex: 1;
}
.toastAction,
.toastClose {
  min-block-size: 36px;
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 700;
  padding-inline: var(--space-2);
}
.toastAction {
  text-decoration: underline;
  text-underline-offset: 3px;
}
@media (min-width: 600px) {
  .toaster {
    inset-block-end: var(--space-5);
  }
}

/* ── States ─────────────────────────────────────────────── */
.skeletonGroup {
  display: grid;
  gap: var(--space-3);
}
.skeleton {
  border-radius: var(--radius-2);
  background: linear-gradient(
    90deg,
    var(--surface-1) 25%,
    var(--surface-2) 50%,
    var(--surface-1) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.3s ease-in-out infinite;
}
@keyframes shimmer {
  to {
    background-position: -200% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton,
  .spinner,
  .spinnerLarge {
    animation-duration: 0s;
  }
}
.centered {
  display: grid;
  place-items: center;
  min-block-size: 40dvh;
}
.empty {
  display: grid;
  justify-items: center;
  text-align: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-4);
}
.emptyIcon {
  display: grid;
  place-items: center;
  inline-size: 64px;
  block-size: 64px;
  border-radius: 50%;
  background: var(--surface-2);
  color: var(--text-2);
  margin-block-end: var(--space-2);
}
.emptyIconError {
  color: var(--danger);
}
.emptyTitle {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--font-size-4);
}
.emptyBody {
  margin: 0 0 var(--space-3);
  color: var(--text-2);
  max-inline-size: 40ch;
}
.banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-2);
  background: var(--surface-2);
  font-weight: 500;
}
.banner-warning {
  background: var(--list-yellow-bg);
  color: var(--list-yellow-fg);
}

.avatarButton {
  display: grid;
  place-items: center;
  inline-size: 40px;
  block-size: 40px;
  border: 0;
  border-radius: 50%;
  background: var(--accent);
  color: var(--accent-fg);
  font: inherit;
  font-weight: 700;
  font-size: var(--font-size-1);
  cursor: pointer;
}
.avatarButton:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: 2px;
}
